import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ArrowRightLeft,
  Boxes,
  ClipboardList,
  Laptop,
  LayoutDashboard,
  Package,
  Settings,
  Wrench,
} from 'lucide-react';
import { cn } from '@hrms/ui';
import type { NavChild, NavItem } from '../../nav.js';
import { ICONS } from '../../navIcons.js';
import { getSidebarNavItemClasses } from '../../sidebarNavStyles.js';

export interface AssetFlyoutItem {
  to: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  badge?: string | number;
  badgeTone?: 'neutral' | 'warning' | 'accent' | 'danger' | 'success' | 'amber' | 'blue';
  subLinks?: readonly { to: string; label: string }[];
  status?: 'live' | 'soon';
}

export const DEFAULT_ASSET_SUBMENU_ITEMS: AssetFlyoutItem[] = [
  {
    to: '/asset-management/dashboard',
    label: 'Dashboard',
    description: 'Overview, analytics & trends',
    icon: <LayoutDashboard className="size-4 text-accent" />,
  },
  {
    to: '/asset-management/assets',
    label: 'Assets',
    description: 'Inventory & personal custody',
    icon: <Boxes className="size-4 text-accent" />,
    subLinks: [
      { to: '/asset-management/assets', label: 'Asset Inventory' },
      { to: '/asset-management/my-assets', label: 'My Assets' },
    ],
  },
  {
    to: '/asset-management/requests',
    label: 'Requests',
    description: 'Approvals & allocation queue',
    icon: <ClipboardList className="size-4 text-info" />,
  },
  {
    to: '/asset-management/operations',
    label: 'Assignments & Transfers',
    description: 'Custody lifecycle & check-in',
    icon: <ArrowRightLeft className="size-4 text-avatar-6-fg" />,
  },
  {
    to: '/asset-management/maintenance',
    label: 'Maintenance',
    description: 'Repairs & active tickets',
    icon: <Wrench className="size-4 text-warning" />,
    badge: 1,
  },
  {
    to: '/asset-management/administration/configuration',
    label: 'Administration',
    description: 'Categories, types, batches & audit',
    icon: <Settings className="size-4 text-avatar-3-fg" />,
    subLinks: [
      { to: '/asset-management/administration/configuration', label: 'Configuration' },
      { to: '/asset-management/administration/vendors', label: 'Vendors' },
      { to: '/asset-management/administration/batches', label: 'Batches' },
      { to: '/asset-management/administration/history', label: 'History & Audit' },
    ],
  },
];

export interface AssetSubmenuFlyoutProps {
  open: boolean;
  pos: { top: number; left: number } | null;
  onClose: () => void;
  items?: readonly NavChild[];
}

export function AssetSubmenuFlyout({ open, pos, onClose, items }: AssetSubmenuFlyoutProps) {
  const location = useLocation();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState<{ top: number; left: number } | null>(pos);

  useEffect(() => {
    setAdjustedPos(pos);
  }, [pos]);

  // Adjust positioning to stay within viewport bounds
  useLayoutEffect(() => {
    if (!pos || !flyoutRef.current) return;
    const flyoutHeight = flyoutRef.current.offsetHeight;
    const maxTop = window.innerHeight - flyoutHeight - 12;
    if (pos.top > maxTop) {
      setAdjustedPos({
        top: Math.max(12, maxTop),
        left: pos.left,
      });
    }
  }, [pos]);

  // Close on scroll or outside click
  useEffect(() => {
    if (!open) return;
    const onScroll = (event: Event) => {
      if (event.target instanceof Node && flyoutRef.current?.contains(event.target)) return;
      onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, onClose]);

  if (!open || !adjustedPos) return null;

  // Build items from props or fallback to default
  const renderItems: AssetFlyoutItem[] =
    items && items.length > 0
      ? items.map((child) => {
          const matchingDefault = DEFAULT_ASSET_SUBMENU_ITEMS.find(
            (d) =>
              d.to === child.to ||
              d.label.toLowerCase() === child.label.toLowerCase() ||
              (d.subLinks && d.subLinks.some((s) => s.to === child.to)),
          );
          return {
            to: child.to,
            label: child.label,
            description: matchingDefault?.description,
            icon: matchingDefault?.icon || <Package className="size-4 text-accent" />,
            badge: child.badge ?? matchingDefault?.badge,
            badgeTone: child.badgeTone ?? matchingDefault?.badgeTone,
            subLinks: child.subLinks ?? matchingDefault?.subLinks,
            status: child.status,
          };
        })
      : DEFAULT_ASSET_SUBMENU_ITEMS;

  return createPortal(
    <div
      ref={flyoutRef}
      style={{
        top: adjustedPos.top,
        left: adjustedPos.left + 8,
        backgroundColor: 'var(--sidebar-submenu-bg)',
      }}
      className={cn(
        'fixed z-50 flex w-60 flex-col rounded-2xl p-2.5 text-white',
        'border border-white/20 shadow-2xl backdrop-blur-md text-xs select-none',
        'animate-in fade-in-0 zoom-in-95 duration-150 ease-out',
      )}
      role="menu"
      aria-label="Asset Management Menu"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      {/* Menu links list */}
      <div className="space-y-1">
        {renderItems.map((item) => {
          const isActive =
            location.pathname === item.to ||
            (item.to !== '/asset-management/dashboard' && location.pathname.startsWith(item.to)) ||
            Boolean(item.subLinks?.some((s) => location.pathname === s.to));

          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              role="menuitem"
              className={cn(
                'flex items-center justify-between px-3 py-2 rounded-xl text-xs transition duration-150',
                isActive
                  ? 'bg-accent text-white font-semibold shadow-sm'
                  : 'text-sidebar-fg-muted hover:text-sidebar-fg hover:bg-sidebar-hover-bg font-medium',
              )}
            >
              <span className="truncate">{item.label}</span>
              <div className="flex items-center gap-1.5 shrink-0">
                {item.badge !== undefined && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-warning/90 text-white">
                    {item.badge}
                  </span>
                )}
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-surface shrink-0" />}
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Navigation item component for AppShell sidebar integration.
 * Triggers the #0B1938 dark-navy AssetSubmenuFlyout on hover, focus, and click.
 */
export function AssetNavItem({ item }: { item: NavItem }) {
  const location = useLocation();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const open = pos !== null;

  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const clearTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };

  const openFlyout = () => {
    clearTimers();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.top, left: rect.right });
  };

  const closeFlyout = () => {
    clearTimers();
    setPos(null);
  };

  const scheduleOpen = () => {
    window.clearTimeout(closeTimer.current);
    if (open) return;
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(openFlyout, 60);
  };

  const scheduleClose = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(closeFlyout, 200);
  };

  useEffect(closeFlyout, [location.pathname]);
  useEffect(() => clearTimers, []);

  // Lit when the current page is one of THIS item's own pages. The menu has two asset entries
  // (the employee's Assets and Admin > Assets), so "anywhere under /asset-management" lit both.
  const isCurrentSectionActive = (item.children ?? []).some(
    (child) => location.pathname === child.to || location.pathname.startsWith(`${child.to}/`),
  );

  return (
    <div
      ref={triggerRef}
      className="relative w-full group"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={openFlyout}
      onBlur={(e) => {
        if (!triggerRef.current?.contains(e.relatedTarget as Node)) {
          closeFlyout();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') closeFlyout();
      }}
    >
      <NavLink
        to={item.to}
        aria-haspopup="menu"
        aria-expanded={open}
        className={({ isActive }) => getSidebarNavItemClasses(isActive || isCurrentSectionActive)}
      >
        {ICONS[item.to] ?? <Laptop className="size-4" />}
        <span className="truncate">{item.label}</span>
      </NavLink>

      <AssetSubmenuFlyout open={open} pos={pos} onClose={closeFlyout} items={item.children} />
    </div>
  );
}

export default AssetSubmenuFlyout;
